import { observable, toJS, flow, action } from "mobx";
import {
	getStrategies,
	createStrategy,
	saveStrategy,
	deleteStrategies,
	duplicateStrategy
} from "services/strategies";
import { registerHandler, delay } from "services/api";
import { message } from "antd";
import _ from "lodash";

export default class StrategiesStore {
	constructor() {
		this.strategies = observable.array([]);
		this.ui = this.getUI();
	}

	getUI = () => {
		let ui = {
			loadingStrategies: false,			
			addEditStrategyDialogVisible: false,
			editedStrategy: null, //When editedStragey is not null, it means that we are rename a strategy
			renamingStrategy: false,
			deletingStrategies: false,
			creatingStrategy: false,
			importingStrategies: false,
			importStrategiesDialogVisible: false,
			duplicatingStrategy: false,
			duplicateStrategyDialogVisible: false,
			duplicatedStrategy: null
		};
		return observable.object(ui);
	};

	@action
	showImportStrategiesDialog = () => {
		this.ui.importStrategiesDialogVisible = true;
	}

	@action
	hideImportStrategiesDialog = () => {
		this.ui.importStrategiesDialogVisible = false;
	}

	@action
	showAddStrategyDialog = () => {
		this.ui.addEditStrategyDialogVisible = true;
		this.ui.editedStrategy = null;
	};

	@action
	hideAddStrategyDialog = () => {
		this.ui.addEditStrategyDialogVisible = false;
		this.ui.editedStrategy = null;
	};

	@action
	showEditStrategyDialog = strategy => {
		this.ui.addEditStrategyDialogVisible = true;
		this.ui.editedStrategy = strategy;
	};

	@action
	hideAddEditStrategyDialog = () => {
		this.ui.addEditStrategyDialogVisible = false;
		this.ui.editedStrategy = null;
	};

	@action
	showDuplicateStrategyDialog = strategy => {
		this.ui.duplicateStrategyDialogVisible = true;
		this.ui.duplicatedStrategy = strategy;
	}

	@action
	hideDuplicateStrategyDialog = () => {
		this.ui.duplicateStrategyDialogVisible = false;
		this.ui.duplicatedStrategy = null;
	}

	createStrategy = flow(function*(name) {
		this.ui.creatingStrategy = true;
		const response = yield createStrategy(name);
		this.ui.creatingStrategy = false;

		if (response == null) {
			message.error("Failed to create new diagram. Unknown error", 3);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		message.success("Successfully created a new diagram", 3);
		this.ui.addEditStrategyDialogVisible = false;
		this.ui.editedStrategy = null;
	});

	remoteRenameStrategy = flow(function*(strategy) {
        this.ui.renamingStrategy = true;
        
		const response = yield saveStrategy({
            id: strategy.id,
            name: strategy.name,
            strategy: null,
            description: null
        });

		this.ui.renamingStrategy = false;
		if (response == null) {
			message.error("Failed to rename the diagram. Unknown error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		message.success("Successfully rename the diagram", 3);
		this.ui.addEditStrategyDialogVisible = false;
		this.ui.editedStrategy = null;
	});

	@action
	renameStrategy = strategy => {
		const foundIndex = this.strategies.findIndex(el => {
			return el.id == strategy.id;
		});

		if (foundIndex != -1) {
			this.strategies[foundIndex].name = strategy.name;
		}
	};

	remoteDuplicateStrategy = flow(function*(strategy) {
        this.ui.duplicatingStrategy = true;
        
		const response = yield duplicateStrategy({
            id: strategy.id,
			name: strategy.name        
		});

		this.ui.duplicatingStrategy = false;
		if (response == null) {
			message.error("Failed to clone the diagram. Unknown error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		message.success("Successfully duplicated the diagram", 3);
		this.ui.duplicateStrategyDialogVisible = false;
		this.ui.duplicateStrategy = null;
	});

	remoteDeleteStrategies = flow(function*(ids) {
		this.ui.deletingStrategies = true;
		const response = yield deleteStrategies(ids);
        this.ui.deletingStrategies = false;
        
		if (response == null) {
			message.error("Failed to delete selected diagrams. Unknown error", 3);
			return;
        }
        
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		message.success("Successfully deleted selected diagrams", 3);
	});

	@action
	deleteStrategies = ids => {
		// this.strategies = observable.array(this.strategies.filter(
		// 	strategy => !ids.includes(strategy.id)
		// ));
		_.remove(this.strategies, strategy => {
			return ids.includes(strategy.id);
		});
	};

	@action
	addOrEditStrategy = strategy => {
		const foundIndex = this.strategies.findIndex(el => {
			return el.id == strategy.id;
		});

		if (foundIndex != -1) {
			this.strategies[foundIndex].name = strategy.name;
		} else {
			this.strategies.unshift(strategy);
		}	
    };    

	loadStrategies = flow(function*() {
		this.ui.loadingStrategies = true;
		const response = yield getStrategies();
		this.ui.loadingStrategies = false;
		if (response == null) {
			message.error("Failed to load strategies. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}
		this.strategies = observable.array(response.strategies);

		registerHandler([
			{
				path: "strategies",
				handler: (err, msg) => {
					if (err != null) return;
					const obj = JSON.parse(msg.body);
					if (obj.action == "addNew") {
						var strategies = obj.strategies;
						for (let i = 0; i < strategies.length; i++) {
							let strategy = strategies[i];
							this.addOrEditStrategy(strategy);
						}						
						return;
					}

					if (obj.action == "rename") {
						this.renameStrategy({
							name: obj.name,
							id: obj.id
						});
						return;
					}

					if (obj.action == "strategiesDeleted") {
                        this.deleteStrategies(obj.ids);
                        return;
                    }                    
				}
			}
		]);
	});
}
